/**
 * EditorialSectionBlock, luxury split-column editorial section.
 *
 * Renders one "chapter" of an article. The left panel is cinematic
 * (cover-image-style with optional eyebrow/title/standfirst overlay).
 * The right panel scrolls naturally and contains rich content blocks
 * (paragraphs, pullquotes, images, galleries, videos) rendered through
 * the existing block library so the editorial system stays unified.
 *
 * Scroll behaviour: pure CSS `position: sticky` on the left column.
 * No scroll listeners, no JS animation. Browser-native, smooth on every
 * surface. The left panel "lives" for the duration of its section and
 * naturally hands off to the next section's left panel on scroll past.
 *
 * Layout variants (block.variant):
 *   • image-left      , left visual sticky, right content scrolls (default)
 *   • image-right     , mirror, content sticky left, visual scrolls right
 *   • fullbleed-opener, full-width visual on top, then a single-column
 *                        editorial column below (for the section's opener)
 *   • flow            , no sticky; visual sits inline at the start of the
 *                        section, content flows below in one column
 *
 * Mobile (≤ 900px): grid collapses to one column for every variant.
 * Visual becomes a 70vh hero with the overlay text sitting at the bottom,
 * then the right-side blocks follow. Sticky is dropped, it's awkward on
 * tall thin viewports. The mobile stack is deliberately designed, not
 * a collapsed desktop layout.
 *
 * Data shape:
 *   {
 *     type: 'editorial_section',
 *     variant: 'image-left',
 *     left: {
 *       media: { src, type: 'image' | 'video', focal: 'center' },
 *       eyebrow:    'optional small caps label',
 *       title:      'optional cover headline',
 *       standfirst: 'optional intro line',
 *       overlay:    'on-image' | 'below'   // text placement
 *     },
 *     right: {
 *       blocks: [ { type, ... }, ... ]      // any block from the article system
 *     }
 *   }
 */
import { useRef, useEffect, useState } from 'react';
import { imgUrl } from '../../utils/imgUrl.js';

const GOLD = '#c9a96e';
const FD = "var(--font-heading-primary, 'Cormorant Garamond', Georgia, serif)";
const FU = "var(--font-body, 'Jost', sans-serif)";
const FS = "var(--font-body, 'Jost', sans-serif)";

// ─── Roman numerals (1-50 covers any plausible article) ────────────────────
const ROMAN = ['','I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII','XIV','XV','XVI','XVII','XVIII','XIX','XX','XXI','XXII','XXIII','XXIV','XXV','XXVI','XXVII','XXVIII','XXIX','XXX','XXXI','XXXII','XXXIII','XXXIV','XXXV','XXXVI','XXXVII','XXXVIII','XXXIX','XL','XLI','XLII','XLIII','XLIV','XLV','XLVI','XLVII','XLVIII','XLIX','L'];

// ─── Chapter ornament, Roman numeral with hairline gold rules ──────────────
// Sits between sections as a magazine-style chapter break. Auto-numbered
// by ArticleBody as it iterates editorial_section blocks (chapterNumber
// passed as prop). Hidden when chapterNumber is 0 or showOrnament=false.
function ChapterOrnament({ number, theme = 'dark' }) {
  if (!number) return null;
  const numeral = ROMAN[number] || String(number);
  const isLight = theme === 'light';
  const ruleColor = isLight ? 'rgba(24,20,16,0.18)' : 'rgba(245,240,232,0.18)';
  const numColor  = isLight ? '#181410' : '#f5f0e8';
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'clamp(20px, 4vw, 56px)',
      padding: 'clamp(64px, 9vw, 128px) clamp(20px, 4vw, 64px)',
      background: 'transparent',
    }}>
      <div aria-hidden style={{ flex: 1, maxWidth: 240, height: 1, background: `linear-gradient(to right, transparent, ${ruleColor})` }} />
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      }}>
        <span style={{
          fontFamily: FU,
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.4em',
          textTransform: 'uppercase',
          color: GOLD,
          opacity: 0.78,
        }}>Chapter</span>
        <span style={{
          fontFamily: FD,
          fontSize: 'clamp(28px, 3.4vw, 44px)',
          fontStyle: 'italic',
          fontWeight: 400,
          letterSpacing: '0.06em',
          color: numColor,
          lineHeight: 1,
        }}>{numeral}</span>
      </div>
      <div aria-hidden style={{ flex: 1, maxWidth: 240, height: 1, background: `linear-gradient(to left, transparent, ${ruleColor})` }} />
    </div>
  );
}

// ─── Fade-in-on-enter hook, IntersectionObserver, no scroll listeners ─────
// Pure CSS would use `view-timeline-name` but Safari support is still
// limited (May 2026). IO is bulletproof and respects prefers-reduced-motion.
function useFadeIn(reduced = false) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(reduced);
  useEffect(() => {
    if (reduced) { setVisible(true); return; }
    if (!ref.current) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { setVisible(true); io.unobserve(e.target); } }),
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [reduced]);
  return [ref, visible];
}

// ─── Visual panel, cinematic cover with optional overlay text ──────────────
function VisualPanel({ left = {}, isSticky = false, isMobile = false, theme = 'dark' }) {
  const media = left.media || {};
  const overlayPos = left.overlay || 'on-image';
  const hasText = !!(left.eyebrow || left.title || left.standfirst);
  // Theme-aware overlay text. Dark theme keeps the magazine-cover white-on-
  // dim-image look. Light theme inverts to dark text + a soft white scrim,
  // so the overlay still reads cleanly when the article is in lights-on
  // (cream background) mode.
  const isLight = theme === 'light';
  const overlayTextColor = isLight ? '#181410' : '#ffffff';
  const overlaySubColor  = isLight ? 'rgba(24,20,16,0.72)' : 'rgba(255,255,255,0.84)';
  const overlayShadow    = isLight ? '0 1px 12px rgba(255,255,255,0.4)' : '0 2px 24px rgba(0,0,0,0.45)';
  const overlayGradient  = isLight
    ? 'linear-gradient(to top, rgba(255,255,255,0.78) 0%, rgba(255,255,255,0.25) 38%, transparent 60%)'
    : 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 38%, transparent 60%)';

  // Base visual container, cinematic, calm, dark, tall.
  // Desktop sticky: 100vh height
  // Mobile hero: 78vh
  // Inline (flow / fullbleed): natural aspect ratio with an explicit max
  // Crop ratio, writer-controlled cinematic shape for the visual.
  // Defaults to 4/5 (current behaviour). Sticky-mode ignores crop and fills
  // the viewport (100vh); the crop only affects flow / fullbleed-opener
  // / mobile hero where the visual sits inline rather than full-screen.
  const cropRatio = left?.media?.crop || '4/5';
  const cropAspect = (cropRatio || '4/5').replace('-', ' / ').replace('_', ' / ');
  const visualStyle = isMobile
    ? { width: '100%', height: cropRatio === '21/9' ? '52vh' : '78vh', minHeight: cropRatio === '21/9' ? 320 : 480 }
    : isSticky
      ? { position: 'sticky', top: 0, height: '100vh', alignSelf: 'start' }
      : { width: '100%', aspectRatio: cropAspect, maxHeight: '88vh' };

  const isVideo = media.type === 'video' || /\.(mp4|webm|mov)(\?|$)/i.test(media.src || '');

  return (
    <div
      style={{
        ...visualStyle,
        position: isSticky ? 'sticky' : 'relative',
        background: '#0a0908',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Media, image or video, fills container with cover crop */}
      <div style={{ position: 'absolute', inset: 0 }}>
        {isVideo && media.src ? (
          <video
            src={media.src}
            autoPlay loop muted playsInline
            poster={imgUrl(media.poster, { width: 1200 }) || undefined}
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: media.focal || 'center',
              display: 'block',
            }}
          />
        ) : media.src ? (
          <img
            src={imgUrl(media.src, { width: 1600 })}
            alt={media.alt || left.title || ''}
            loading="lazy"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: media.focal || 'center',
              display: 'block',
            }}
          />
        ) : (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(160deg, #1a1714 0%, #0a0908 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'rgba(245,240,232,0.18)',
            fontFamily: FU, fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          }}>
            Visual placeholder
          </div>
        )}
      </div>

      {/* Subtle bottom-up gradient, keeps overlay text readable on bright photos.
          Only applied when overlay is on-image AND there's text to show.
          Direction inverts in light theme (white scrim instead of black). */}
      {hasText && overlayPos === 'on-image' && (
        <div
          aria-hidden
          style={{
            position: 'absolute', inset: 0,
            background: overlayGradient,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Overlay text, sits over the image with a soft scrim. Theme-aware:
          dark mode keeps the magazine-cover white-on-image look; light mode
          flips to dark text on a soft white scrim so it still reads in
          lights-on viewing. */}
      {hasText && overlayPos === 'on-image' && (
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            marginTop: 'auto',
            padding: isMobile ? 'clamp(20px, 5vw, 32px)' : 'clamp(32px, 4vw, 56px)',
            color: overlayTextColor,
            maxWidth: 720,
          }}
        >
          {left.eyebrow && (
            <div style={{
              fontFamily: FU,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: GOLD,
              marginBottom: 14,
              textShadow: overlayShadow,
            }}>
              {left.eyebrow}
            </div>
          )}
          {left.title && (
            <h2 style={{
              fontFamily: FD,
              fontWeight: 400,
              fontStyle: 'italic',
              fontSize: 'clamp(32px, 4.4vw, 64px)',
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              margin: '0 0 16px',
              color: overlayTextColor,
              textShadow: overlayShadow,
            }}>
              {left.title}
            </h2>
          )}
          {left.standfirst && (
            <p style={{
              fontFamily: FD,
              fontWeight: 300,
              fontSize: 'clamp(15px, 1.4vw, 19px)',
              lineHeight: 1.55,
              color: overlaySubColor,
              margin: 0,
              maxWidth: 480,
              textShadow: overlayShadow,
            }}>
              {left.standfirst}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Below-image text strip (when overlay='below') ──────────────────────────
function VisualText({ left = {}, theme = 'dark' }) {
  const hasText = !!(left.eyebrow || left.title || left.standfirst);
  if (!hasText) return null;
  const titleColor = theme === 'light' ? '#181410' : '#f5f0e8';
  const standColor = theme === 'light' ? 'rgba(24,20,16,0.72)' : 'rgba(245,240,232,0.78)';
  return (
    <div style={{
      padding: 'clamp(20px, 3vw, 36px) clamp(20px, 3vw, 32px)',
      maxWidth: 560,
    }}>
      {left.eyebrow && (
        <div style={{
          fontFamily: FU,
          fontSize: 10, fontWeight: 600,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD,
          marginBottom: 12,
        }}>
          {left.eyebrow}
        </div>
      )}
      {left.title && (
        <h2 style={{
          fontFamily: FD,
          fontWeight: 400,
          fontStyle: 'italic',
          fontSize: 'clamp(28px, 3vw, 44px)',
          lineHeight: 1.1,
          letterSpacing: '-0.015em',
          margin: '0 0 14px',
          color: titleColor,
        }}>
          {left.title}
        </h2>
      )}
      {left.standfirst && (
        <p style={{
          fontFamily: FD,
          fontWeight: 300,
          fontSize: 'clamp(14px, 1.3vw, 18px)',
          lineHeight: 1.55,
          color: standColor,
          margin: 0,
        }}>
          {left.standfirst}
        </p>
      )}
    </div>
  );
}

// ─── Right column, editorial rhythm: generous spacing, drop-cap option ────
// Renders the section's right-side content with editorial typography. Each
// child block is rendered through the renderRightBlock prop, which the
// parent (EditorialArticlePage) wires to its own block dispatcher so we
// reuse the existing image/gallery/video/etc. rendering.
function ContentColumn({ blocks = [], renderRightBlock, theme, dropCap = false }) {
  if (!blocks.length) return null;

  return (
    <div
      className="ed-section-column"
      style={{
        // Editorial rhythm: large reading width, generous padding.
        // Caps content width so it stays readable even on huge displays.
        maxWidth: 720,
        width: '100%',
        padding: 'clamp(48px, 7vw, 96px) clamp(20px, 4vw, 64px)',
      }}
    >
      <style>{`
        /* Drop-cap: large illuminated initial on the first paragraph.
           Only applies to the FIRST <p> child of an editorial section's
           content column when dropCap is enabled. */
        .ed-section-column.has-dropcap > :first-child::first-letter,
        .ed-section-column.has-dropcap p:first-of-type::first-letter {
          font-family: ${FD};
          font-weight: 400;
          font-style: italic;
          float: left;
          font-size: 5.4em;
          line-height: 0.86;
          padding-right: 12px;
          padding-top: 6px;
          color: ${GOLD};
        }
      `}</style>
      <div className={dropCap ? 'has-dropcap' : ''}>
        {blocks.map((block, i) => (
          <div key={block.id ?? i} style={{ marginBottom: 'clamp(20px, 2.4vw, 36px)' }}>
            {renderRightBlock ? renderRightBlock(block, i) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main EditorialSectionBlock ─────────────────────────────────────────────
// Receives the full block object and a renderRightBlock callback. The
// callback is provided by the article-level ArticleBody so we don't have
// to duplicate block-rendering logic in this file, gallery/video/image/
// pullquote etc. all stay in their existing renderers.
export default function EditorialSectionBlock({
  block,
  renderRightBlock,
  isMobile = false,
  theme = 'dark',
  chapterNumber = 0,         // auto-numbered by ArticleBody. 0 = no ornament.
}) {
  const variant   = block?.variant   || 'image-left';
  const left      = block?.left      || {};
  const right     = block?.right     || { blocks: [] };
  const dropCap   = !!block?.dropCap;
  const showOrnament = block?.showOrnament !== false;  // default on
  const overlayBelow = (left?.overlay || 'on-image') === 'below';
  // Fade-in on viewport enter, respects prefers-reduced-motion.
  const reducedMotion = typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [fadeRef, isVisible] = useFadeIn(reducedMotion);
  const fadeStyle = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(14px)',
    transition: 'opacity 880ms cubic-bezier(0.2, 0.7, 0.2, 1), transform 880ms cubic-bezier(0.2, 0.7, 0.2, 1)',
    willChange: 'opacity, transform',
  };
  const sectionRef = useRef(null);

  // Mobile: always stack as a deliberate magazine layout, regardless of variant.
  // Visual hero on top, eyebrow/title/standfirst overlay, then content.
  if (isMobile) {
    return (
      <>
        {showOrnament && chapterNumber > 0 && <ChapterOrnament number={chapterNumber} theme={theme} />}
        <section
          ref={fadeRef}
          style={{
            margin: '0 0 clamp(48px, 8vw, 96px)',
            background: theme === 'light' ? '#FAF8F4' : '#0a0908',
            color: theme === 'light' ? '#181410' : '#f5f0e8',
            ...fadeStyle,
          }}
        >
          <VisualPanel left={left} isSticky={false} isMobile={true} theme={theme} />
          {overlayBelow && <VisualText left={left} theme={theme} />}
          <ContentColumn
            blocks={right.blocks || []}
            renderRightBlock={renderRightBlock}
            theme={theme}
            dropCap={dropCap}
          />
        </section>
      </>
    );
  }

  // Desktop / tablet (≥ 900px), variant-driven layouts.
  // Each variant uses the same building blocks (VisualPanel + ContentColumn)
  // arranged differently. CSS Grid handles the column ratios.

  // Variant: full-bleed opener, visual spans full width, content below in
  // a single readable column. Used for chapter openers / hero moments.
  if (variant === 'fullbleed-opener') {
    return (
      <>
        {showOrnament && chapterNumber > 0 && <ChapterOrnament number={chapterNumber} theme={theme} />}
        <section
          ref={fadeRef}
          style={{
            margin: '0 0 clamp(48px, 8vw, 96px)',
            background: theme === 'light' ? '#FAF8F4' : '#0a0908',
            color: theme === 'light' ? '#181410' : '#f5f0e8',
            ...fadeStyle,
          }}
        >
          <VisualPanel left={left} isSticky={false} isMobile={false} theme={theme} />
          {overlayBelow && <VisualText left={left} theme={theme} />}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ContentColumn
              blocks={right.blocks || []}
              renderRightBlock={renderRightBlock}
              theme={theme}
              dropCap={dropCap}
            />
          </div>
        </section>
      </>
    );
  }

  // Variant: flow, minimal inline layout (no sticky). Visual sits at the
  // top of the section's column, content flows below. Use when you want
  // a section break without the cinematic split feel.
  if (variant === 'flow') {
    return (
      <>
        {showOrnament && chapterNumber > 0 && <ChapterOrnament number={chapterNumber} theme={theme} />}
        <section
          ref={fadeRef}
          style={{
            margin: '0 0 clamp(48px, 8vw, 96px)',
            background: theme === 'light' ? '#FAF8F4' : '#0a0908',
            color: theme === 'light' ? '#181410' : '#f5f0e8',
            display: 'flex',
            justifyContent: 'center',
            ...fadeStyle,
          }}
        >
          <div style={{ maxWidth: 800, width: '100%' }}>
            {left?.media?.src && (
              <div style={{ aspectRatio: '4 / 3', maxHeight: '70vh', overflow: 'hidden', position: 'relative' }}>
                <VisualPanel left={left} isSticky={false} isMobile={false} theme={theme} />
              </div>
            )}
            {overlayBelow && <VisualText left={left} theme={theme} />}
            <ContentColumn
              blocks={right.blocks || []}
              renderRightBlock={renderRightBlock}
              theme={theme}
              dropCap={dropCap}
            />
          </div>
        </section>
      </>
    );
  }

  // Variants: image-left | image-right, sticky split layout
  // The left and right columns are real grid children; sticky is done with
  // position: sticky on the visual column, scoped to this section's grid.
  const isMirrored = variant === 'image-right';

  return (
    <>
      {showOrnament && chapterNumber > 0 && <ChapterOrnament number={chapterNumber} theme={theme} />}
      <section
        ref={fadeRef}
        style={{
          // min-height: 100vh ensures the sticky has room to live for the
          // whole section. Content shorter than a viewport will still hold
          // its sticky frame for one screen, then release.
          minHeight: '100vh',
          margin: 0,                  /* sections butt up to each other */
          padding: 0,
          background: theme === 'light' ? '#FAF8F4' : '#0a0908',
          color: theme === 'light' ? '#181410' : '#f5f0e8',
          // No overflow on the section, overflow:hidden breaks position:sticky
          // on iOS Safari. Let the children breathe.
          ...fadeStyle,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMirrored ? 'minmax(0, 1.25fr) minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1.25fr)',
            gap: 0,
            alignItems: 'start',
          }}
        >
          {/* Visual column, sticky on the side it lives on */}
          <div style={{ order: isMirrored ? 2 : 1 }}>
            <VisualPanel left={left} isSticky={true} isMobile={false} theme={theme} />
            {overlayBelow && <VisualText left={left} theme={theme} />}
          </div>

          {/* Content column, scrolls naturally */}
          <div style={{
            order: isMirrored ? 1 : 2,
            display: 'flex',
            justifyContent: isMirrored ? 'flex-end' : 'flex-start',
          }}>
            <ContentColumn
              blocks={right.blocks || []}
              renderRightBlock={renderRightBlock}
              theme={theme}
              dropCap={dropCap}
            />
          </div>
        </div>
      </section>
    </>
  );
}
